import * as Sentry from "@sentry/angular";
import { Injectable, type Signal, inject, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { liveQuery } from "dexie";
import { from, of, switchMap } from "rxjs";
import { AuthService } from "./auth.service";
import { GameUploadService } from "./game-upload.service";
import {
	type MagpieDatabase,
	type PhotoOutboxEntry,
	getMagpieDb,
} from "./storage.service";

type PhotoSource =
	| { kind: "picsum"; seed: string }
	| { kind: "blob"; blob: Blob };

const BACKOFF_MS = [
	1_000, 2_000, 5_000, 15_000, 30_000, 60_000, 60_000, 60_000,
];
const MAX_ATTEMPTS = BACKOFF_MS.length;

@Injectable({
	providedIn: "root",
})
export class PhotoOutboxService {
	private readonly upload = inject(GameUploadService);
	private readonly auth = inject(AuthService);
	private readonly db: MagpieDatabase | null = getMagpieDb();

	private readonly activeSession = signal<string | null>(null);
	private pumping = false;
	private pumpTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly blobUrls = new Map<string, string>();

	readonly sessionEntries: Signal<PhotoOutboxEntry[]> = toSignal(
		toObservable(this.activeSession).pipe(
			switchMap((sid) => {
				if (!sid || !this.db) return of<PhotoOutboxEntry[]>([]);
				return from(
					liveQuery(() =>
						this.db!.photoOutbox.where("sessionId").equals(sid).toArray(),
					),
				);
			}),
		),
		{ initialValue: [] },
	);

	constructor() {
		if (typeof window !== "undefined") {
			window.addEventListener("online", () => this.pump());
		}
		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "visible") this.pump();
			});
		}
	}

	setActiveSession(sessionId: string | null): void {
		this.activeSession.set(sessionId);
	}

	/**
	 * Returns the cached blob: URL for a (roundId, itemId) within the active
	 * session, or null. Reads `sessionEntries()`, so template renders that
	 * call it auto-track and re-render when the outbox updates.
	 */
	photoFor(roundId: string, itemId: string): string | null {
		const match = this.entryFor(roundId, itemId);
		if (!match) return null;
		return this.ensureBlobUrl(match.id, match.blob);
	}

	entryFor(roundId: string, itemId: string): PhotoOutboxEntry | null {
		return (
			this.sessionEntries().find(
				(e) => e.roundId === roundId && e.itemId === itemId,
			) ?? null
		);
	}

	async enqueuePhoto(
		sessionId: string,
		roundId: string,
		itemId: string,
		source: PhotoSource,
	): Promise<void> {
		if (!this.db) return;

		const blob = await this.resolveSource(source);

		const existing = await this.db.photoOutbox
			.where("[sessionId+roundId+itemId]")
			.equals([sessionId, roundId, itemId])
			.first();

		if (existing) {
			const oldUrl = this.blobUrls.get(existing.id);
			if (oldUrl) {
				URL.revokeObjectURL(oldUrl);
				this.blobUrls.delete(existing.id);
			}
			await this.db.photoOutbox.update(existing.id, {
				blob,
				contentType: blob.type || "image/jpeg",
				sizeBytes: blob.size,
				status: "queued" as const,
				attempts: 0,
				nextAttemptAt: 0,
				createdAt: Date.now(),
				photoKey: undefined,
				submissionId: undefined,
				broadcastAt: undefined,
				uploadedAt: undefined,
				lastError: undefined,
			});
		} else {
			await this.db.photoOutbox.add({
				id: crypto.randomUUID(),
				sessionId,
				roundId,
				itemId,
				blob,
				contentType: blob.type || "image/jpeg",
				sizeBytes: blob.size,
				status: "queued",
				attempts: 0,
				nextAttemptAt: 0,
				createdAt: Date.now(),
			});
		}

		this.pump();
	}

	async retry(id: string): Promise<void> {
		if (!this.db) return;
		await this.db.photoOutbox.update(id, {
			status: "queued",
			attempts: 0,
			nextAttemptAt: 0,
			lastError: undefined,
		});
		this.pump();
	}

	/**
	 * DEV-ONLY: drop every outbox entry for a session and revoke their
	 * blob: URLs. Used by the "Restart" flow so the grid comes back
	 * empty when we rewind a session.
	 */
	async clearSession(sessionId: string): Promise<void> {
		if (!this.db) return;
		const rows = await this.db.photoOutbox
			.where("sessionId")
			.equals(sessionId)
			.toArray();
		for (const row of rows) {
			const url = this.blobUrls.get(row.id);
			if (url) {
				URL.revokeObjectURL(url);
				this.blobUrls.delete(row.id);
			}
		}
		await this.db.photoOutbox.where("sessionId").equals(sessionId).delete();
	}

	async resume(): Promise<void> {
		if (!this.db) return;
		await this.db.photoOutbox
			.where("status")
			.equals("uploading")
			.modify({ status: "queued", nextAttemptAt: 0 });
		this.pump();
	}

	private async resolveSource(source: PhotoSource): Promise<Blob> {
		if (source.kind === "blob") return source.blob;
		const url = `https://picsum.photos/seed/${encodeURIComponent(source.seed)}/1200/1200`;
		const res = await fetch(url);
		if (!res.ok) {
			throw new Error(`Failed to fetch picsum (${res.status})`);
		}
		return await res.blob();
	}

	private ensureBlobUrl(id: string, blob: Blob): string {
		const existing = this.blobUrls.get(id);
		if (existing) return existing;
		const url = URL.createObjectURL(blob);
		this.blobUrls.set(id, url);
		return url;
	}

	private async pump(): Promise<void> {
		if (this.pumping || !this.db) return;
		this.pumping = true;
		try {
			while (true) {
				const now = Date.now();
				const entry = await this.db.photoOutbox
					.where("status")
					.anyOf(["queued", "uploading"])
					.filter((e) => e.nextAttemptAt <= now)
					.first();
				if (!entry) break;
				await this.processEntry(entry);
			}
			await this.scheduleNext();
		} finally {
			this.pumping = false;
		}
	}

	private async scheduleNext(): Promise<void> {
		if (this.pumpTimer) {
			clearTimeout(this.pumpTimer);
			this.pumpTimer = null;
		}
		if (!this.db) return;
		const pending = await this.db.photoOutbox
			.where("status")
			.anyOf(["queued", "uploading"])
			.sortBy("nextAttemptAt");
		const next = pending[0];
		if (!next) return;
		const delay = Math.max(0, next.nextAttemptAt - Date.now());
		this.pumpTimer = setTimeout(() => this.pump(), delay);
	}

	private async processEntry(entry: PhotoOutboxEntry): Promise<void> {
		if (!this.db) return;
		await this.db.photoOutbox.update(entry.id, { status: "uploading" });

		try {
			let photoKey = entry.photoKey;
			if (!photoKey) {
				const { uploadUrl, objectKey } = await this.upload.requestPresignedUrl(
					entry.sessionId,
					entry.roundId,
					entry.itemId,
					entry.blob,
				);
				await this.upload.putToR2(uploadUrl, entry.blob);
				photoKey = objectKey;
				await this.db.photoOutbox.update(entry.id, { photoKey });
			}

			let submissionId = entry.submissionId;
			if (!submissionId) {
				const submission = await this.upload.recordSubmission(
					entry.sessionId,
					entry.roundId,
					entry.itemId,
					photoKey,
				);
				submissionId = submission.id;
				await this.db.photoOutbox.update(entry.id, { submissionId });
			}

			if (!entry.broadcastAt) {
				const userId = this.auth.currentUser()?.id;
				if (!userId) throw new Error("No authenticated user");
				this.upload.broadcast(
					submissionId,
					userId,
					entry.roundId,
					entry.itemId,
					photoKey,
				);
				const now = Date.now();
				await this.db.photoOutbox.update(entry.id, {
					status: "uploaded",
					broadcastAt: now,
					uploadedAt: now,
				});
			}
		} catch (err) {
			Sentry.withScope((scope) => {
				const user = this.auth.currentUser();
				if (user) scope.setUser({ id: user.id, email: user.email });
				scope.setContext("photoUpload", {
					sessionId: entry.sessionId,
					roundId: entry.roundId,
					itemId: entry.itemId,
					sizeBytes: entry.sizeBytes,
					attempts: entry.attempts,
				});
				Sentry.captureException(err);
			});
			await this.recordFailure(entry, err);
		}
	}

	private async recordFailure(
		entry: PhotoOutboxEntry,
		err: unknown,
	): Promise<void> {
		if (!this.db) return;
		const attempts = entry.attempts + 1;
		const message = err instanceof Error ? err.message : String(err);
		const patch: Partial<PhotoOutboxEntry> = { attempts, lastError: message };
		if (attempts >= MAX_ATTEMPTS) {
			patch.status = "failed";
		} else {
			patch.status = "queued";
			patch.nextAttemptAt = Date.now() + BACKOFF_MS[attempts - 1];
		}
		await this.db.photoOutbox.update(entry.id, patch);
	}
}
